
import React from 'react';
import { Helmet } from 'react-helmet';
import { Database } from '@/integrations/supabase/types';

// Update the type to be consistent with other components
type BlogPostWithAuthor = Partial<Database['public']['Tables']['blog_posts']['Row']> & {
  author?: {
    first_name?: string;
    last_name?: string;
  } | null;
  tags?: { id: string; name: string }[];
};

interface BlogOpenGraphProps {
  post: BlogPostWithAuthor;
  baseUrl: string;
}

export function BlogOpenGraph({ post, baseUrl }: BlogOpenGraphProps) {
  // Determine the title, description and image with fallbacks
  const title = post.seo_title || post.title || '';
  const description = post.seo_description || post.excerpt || `Read more about ${post.title || ''}`;
  const imageUrl = post.featured_image || `${baseUrl}/og-image.png`;
  
  // Get publication date
  const publishedDate = post.created_at 
    ? new Date(post.created_at).toISOString() 
    : new Date().toISOString();
  
  // Get author name
  const authorName = post.author 
    ? `${post.author.first_name || ''} ${post.author.last_name || ''}`.trim() 
    : 'Trotting the World';
    
  // Get tags as keywords
  const keywords = post.seo_keywords || (post.tags ? post.tags.map(tag => tag.name).join(', ') : '');

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`${baseUrl}/blog/${post.slug || ''}`} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="article:published_time" content={publishedDate} />
      <meta property="article:author" content={authorName} />
      {post.tags && post.tags.map((tag, index) => (
        <meta key={index} property="article:tag" content={tag.name} />
      ))}
      
      {/* Canonical URL */}
      <link rel="canonical" href={`${baseUrl}/blog/${post.slug || ''}`} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={`${baseUrl}/blog/${post.slug || ''}`} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
    </Helmet>
  );
}
